import { useMemo, useState } from "react";
import { useAccount, usePublicClient, useReadContract, useWriteContract } from "wagmi";
import { bsc } from "wagmi/chains";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { parseAbi, parseEventLogs } from "viem";
import { toast } from "react-toastify";
import { AlertTriangle } from "lucide-react";
import Web3Provider from "../../../../components/Web3Provider";
import { Button } from "../../../../components/ui/button";
import { ErrorBanner } from "../../hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../components/QueueLayout";
import { QueueTable, Td } from "../components/table";
import { CopyHash } from "../components/CopyHash";
import { labelCls } from "../components/FormDialog";
import { useUncollected, useCollections, useRecordCollection } from "../api/hooks";
import { formatDateTime } from "../../../../lib/format";

// Sweeping funded deposit addresses into the Ginox wallet.
//
// This is treasury work, not customer support: a deposit is credited to the user's
// MT5 account long before it is collected, so nothing here affects any user. It only
// moves the USDT off the single-use deposit addresses.
//
// The server never signs and pins no destination — the admin signs from their own
// wallet, and the backend only READS the receipt afterwards. So the flow is:
//   select rows -> batchSweep(salts, token, to) -> POST /collections { txHash }
//
// Simpler than the GUSD sweep this is modelled on, because our backend does more:
// no client-side event decoding (the backend reads the receipt), no zero-balance
// filtering (uncollected only returns funded rows) and no salt computation (it comes
// on the row).

const SWEEP_CONTRACT = "0xf16200E7bc6b9E44B9BA7F0Be6c74cc5a20DB0f8";

// The token to sweep. THIS MUST MATCH the token the deposits were actually made in
// — which is whatever the backend's USDT_CONTRACT_ADDRESS is set to, since that is
// the contract it watches and reads `onChainBalance` from.
//
// batchSweep takes the token as an argument, so the wrong one is not an error: the
// transaction succeeds, sweeps ZERO, burns gas, and the money stays stranded on the
// deposit addresses. Verified on chain — the stage deposit addresses hold SELF and
// exactly 0 real USDT.
//
// Swapping these two lines is the one edit needed before production, and it has to
// happen in lockstep with the backend's own env.
// ponytail: better still, have the backend publish the token on /uncollected.
// prod — real BEP-20 USDT
// const SWEEP_TOKEN = "0x55d398326f99059fF775485246999027B3197955";
// stage — SELF ("Self Crypto"), stands in for USDT. 18 decimals, same as USDT.
const SWEEP_TOKEN = "0x308299bb3f6a7274Ad82bFCFf0739C88aafA3aD7";

const SWEEP_ABI = parseAbi([
  "function batchSweep(bytes32[] salts, address token, address to) returns (uint256 totalAmount, uint256 sweptCount)",
  "function owner() view returns (address)",
  "event Swept(bytes32 indexed salt, address indexed depositAddress, address indexed to, address token, uint256 amount)",
]);

// The gas estimate for batchSweep CANNOT BE TRUSTED, so it gets a buffer.
//
// batchSweep swallows a per-salt failure — it skips that salt and carries on rather
// than reverting. So the transaction succeeds at ANY gas limit, sweeping fewer rows
// (or none) as gas runs short. eth_estimateGas only asks "did it revert?", which is
// true even at 50,000 gas, so it returns a figure BELOW what the work actually costs.
//
// Measured on this contract with 20 SELF present:
//   estimate 233,288 -> swept 0     235,500 -> swept 1
//   4 rows: estimate 834,320 -> swept 3 of 4
//
// This is what produced two live sweeps that succeeded, emitted no events and moved
// nothing. MetaMask adds no buffer of its own — both transactions were sent at the
// raw estimate. 5% covered every batch size measured; 25% is the comfortable one.
// Do NOT "simplify" this back to letting the wallet estimate.
const GAS_BUFFER_NUM = 125n;
const GAS_BUFFER_DEN = 100n;

// Two decimals for display.
//
// The chain figures come back at 18 decimals ("200.000000000000000000"), which is
// exact but unreadable in a table. TRUNCATED, not rounded, and never parsed through
// a float: 18 decimals exceeds float precision, and the backend truncates to cents
// for its own database figure, so this matches it.
//
// Deliberately not used on gasFee — that is BNB, where the interesting digits are
// well past the second decimal place.
const fixed2 = (value) => {
  const text = String(value ?? "").trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) return "—";
  const negative = text.startsWith("-");
  const [whole, frac = ""] = (negative ? text.slice(1) : text).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}.${frac.padEnd(2, "0").slice(0, 2)}`;
};

// Total of the selected rows, in cents so 18 decimals never touch a float. Truncates
// per row exactly as fixed2 does, so the figure on the button is the sum of the
// figures in the column above it rather than a slightly different number.
const totalCents = (values) =>
  values.reduce((sum, value) => {
    const text = String(value ?? "").trim();
    if (!/^\d+(\.\d+)?$/.test(text)) return sum;
    const [whole, frac = ""] = text.split(".");
    return sum + Number(whole) * 100 + Number(frac.padEnd(2, "0").slice(0, 2));
  }, 0);

const UNCOLLECTED_COLUMNS = [
  { label: "" },
  { label: "Ref", align: "right" },
  { label: "User" },
  { label: "Deposit address" },
  { label: "Recorded", align: "right" },
  { label: "On chain", align: "right" },
];

const HISTORY_COLUMNS = [
  { label: "Swept" },
  { label: "Ref", align: "right" },
  { label: "User" },
  { label: "From" },
  { label: "To" },
  { label: "Amount", align: "right" },
  { label: "Gas fee", align: "right" },
  { label: "Tx" },
];

function SweepPage() {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient({ chainId: bsc.id });
  const { writeContractAsync } = useWriteContract();

  const uncollected = useUncollected();
  const history = useCollections();
  const record = useRecordCollection();

  const [selected, setSelected] = useState([]);
  const [sweeping, setSweeping] = useState(false);

  // Read straight from BSC regardless of which chain the wallet is on.
  const { data: contractOwner } = useReadContract({
    address: SWEEP_CONTRACT,
    abi: SWEEP_ABI,
    functionName: "owner",
    chainId: bsc.id,
  });

  // batchSweep is owner-only — verified on chain. Checking here means a wrong
  // wallet is told so up front rather than after selecting rows and eating a revert.
  const isOwner = useMemo(
    () =>
      Boolean(contractOwner) &&
      Boolean(address) &&
      contractOwner.toLowerCase() === address.toLowerCase(),
    [contractOwner, address],
  );

  const rows = uncollected.data?.deposits ?? [];
  const totals = uncollected.data?.totals;
  const historyRows = history.data?.pages.flatMap((p) => p.entries ?? []) ?? [];
  const allTime = history.data?.pages[0]?.totals?.allTime;

  const selectedBalances = rows
    .filter((r) => selected.includes(r.id))
    .map((r) => r.onChainBalance);

  // No destination field: the money goes to the wallet that signs. batchSweep would
  // accept any `to`, but only the owner can call it, so a free-text destination only
  // ever offered a way to mistype an address and send the treasury somewhere else.
  const canSweep = isConnected && isOwner && selected.length > 0 && !sweeping;

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setSelected((prev) => (prev.length === rows.length ? [] : rows.map((r) => r.id)));

  // Set when a sweep landed on chain but could not be recorded. The money HAS
  // moved; only the bookkeeping is outstanding.
  const [unrecorded, setUnrecorded] = useState(null);

  const recordSweep = async (hash) => {
    try {
      // Only the hash is sent: the backend reads the receipt and writes only what
      // the events prove.
      const result = await record.mutateAsync(hash);
      setUnrecorded(null);

      if (result?.written > 0) {
        toast.success(`Recorded ${result.written} sweep${result.written > 1 ? "s" : ""}.`);
      } else {
        toast.warning(
          "The transaction confirmed but nothing was recorded — no sweep events matched a deposit.",
        );
      }
      if (result?.unmatchedSalts?.length) {
        toast.warning(
          `${result.unmatchedSalts.length} salt(s) in this transaction matched no deposit and were not stored.`,
          { autoClose: false },
        );
      }
    } catch (error) {
      setUnrecorded(hash);
      toast.error(
        `The sweep went through but could not be recorded: ${error?.message || "unknown error"}. The money has moved — record it below rather than sweeping again.`,
        { autoClose: false },
      );
    }
  };

  const sweep = async () => {
    if (!canSweep) return;
    const chosen = rows.filter((r) => selected.includes(r.id));

    setSweeping(true);
    try {
      toast.info("Confirm the transaction in your wallet…");

      const args = [chosen.map((r) => r.salt), SWEEP_TOKEN, address];

      // Estimate here and send an explicit limit — see GAS_BUFFER_NUM above for why
      // the bare estimate silently sweeps nothing. A failure to estimate at all is a
      // real revert (wrong owner, bad destination) and belongs in the outer catch.
      const estimate = await publicClient.estimateContractGas({
        address: SWEEP_CONTRACT,
        abi: SWEEP_ABI,
        functionName: "batchSweep",
        args,
        account: address,
      });

      const hash = await writeContractAsync({
        address: SWEEP_CONTRACT,
        abi: SWEEP_ABI,
        functionName: "batchSweep",
        args,
        gas: (estimate * GAS_BUFFER_NUM) / GAS_BUFFER_DEN,
      });

      toast.success(
        <span>
          Sweep submitted.{" "}
          <a
            href={`https://bscscan.com/tx/${hash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            View on BscScan
          </a>
        </span>,
      );
      setSelected([]);

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") {
        toast.error("The transaction reverted on chain. Nothing was swept.");
        return;
      }

      // A successful receipt is NOT proof of a sweep on this contract — a starved or
      // skipped salt leaves a status-1 transaction with no events at all. The `Swept`
      // events are the only honest record of what moved, so they decide what happens
      // next. Without this the empty sweeps went straight to the recorder, which
      // rejected them, and the banner then blamed the recording for money that had
      // never moved.
      const swept = parseEventLogs({
        abi: SWEEP_ABI,
        eventName: "Swept",
        logs: receipt.logs,
      });

      if (swept.length === 0) {
        toast.error(
          "The transaction succeeded but swept nothing — no addresses were drained. The rows are untouched, so try again. Nothing has been recorded.",
          { autoClose: false },
        );
        return;
      }

      // Fewer events than rows means the rest were skipped, not swept. They stay in
      // the list and can go again; saying so beats letting the count quietly drop.
      if (swept.length < chosen.length) {
        toast.warn(
          `Only ${swept.length} of ${chosen.length} addresses were swept. The rest are untouched and still listed.`,
          { autoClose: false },
        );
      }

      // Recording is its OWN failure domain, deliberately.
      //
      // By this point the money has already moved on chain. If recording fails —
      // the node has not caught up, the backend cannot read the chain — folding it
      // into the outer catch would show a generic error and invite the admin to
      // sweep again, moving nothing but wasting gas and confusing the books. So the
      // hash is kept and offered for a retry instead, which is safe because
      // re-posting a hash writes nothing twice.
      await recordSweep(hash);
    } catch (error) {
      const message =
        error?.shortMessage || error?.message?.split("\n")[0] || "The sweep failed";
      if (!/rejected|denied/i.test(message)) toast.error(message);
    } finally {
      setSweeping(false);
    }
  };

  return (
    <QueueLayout
      title="Deposit"
      accent="sweep"
      description="Funded deposit addresses waiting to be drained into the Ginox wallet. Collection is independent of crediting — every user here already has their money."
      isFetching={uncollected.isFetching}
      onRefresh={() => uncollected.refetch()}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-md border border-border/70 bg-white/[0.02] p-4">
          <p className={labelCls}>Wallet</p>
          <ConnectButton showBalance={false} chainStatus="icon" />
          {isConnected ? (
            <p
              className={`mt-2 text-xs font-medium ${isOwner ? "text-emerald-400" : "text-red-400"}`}
            >
              {isOwner
                ? "Contract owner verified — swept money lands here"
                : "Not the contract owner — sweeps will revert"}
            </p>
          ) : null}
        </div>

        {/* Two views of the same money: the database figure truncates to cents, the
            chain figure is exact at 18 decimals. They can differ legitimately, so
            they are never merged into one number. */}
        <div className="rounded-md border border-border/70 bg-white/[0.02] p-4">
          <p className={labelCls}>Uncollected</p>
          <p className="font-mono text-2xl text-ginox-green">
            {fixed2(totals?.database)}
          </p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {fixed2(totals?.onChain)} on chain · {totals?.count ?? 0} address
            {totals?.count === 1 ? "" : "es"}
          </p>
        </div>

        <div className="rounded-md border border-border/70 bg-white/[0.02] p-4">
          <p className={labelCls}>Swept all time</p>
          <p className="font-mono text-2xl text-foreground">{fixed2(allTime?.amount)}</p>
          {/* USDT and BNB — never totalled together. */}
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {allTime?.gasFee ?? "—"} BNB gas · {allTime?.count ?? 0} sweeps
          </p>
        </div>
      </div>

      {unrecorded ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-300">
              A sweep is unrecorded
            </p>
            <p className="mt-0.5 text-xs text-amber-200/70">
              The money moved on chain but the backend has not recorded it. Re-posting
              is safe — it writes nothing twice.
            </p>
            <div className="mt-1.5">
              <CopyHash
                value={unrecorded}
                href={`https://bscscan.com/tx/${unrecorded}`}
                head={10}
                tail={8}
              />
            </div>
          </div>
          <Button
            size="sm"
            disabled={record.isPending}
            onClick={() => recordSweep(unrecorded)}
          >
            {record.isPending ? "Recording…" : "Record it"}
          </Button>
        </div>
      ) : null}

      {uncollected.error ? (
        <ErrorBanner error={uncollected.error} onRetry={() => uncollected.refetch()} />
      ) : (
        <QueueTable
          columns={UNCOLLECTED_COLUMNS}
          isLoading={uncollected.isLoading}
          rows={rows}
          renderRow={(d) => (
            <tr key={d.id} className="transition-colors hover:bg-white/[0.02]">
              <Td>
                <input
                  type="checkbox"
                  checked={selected.includes(d.id)}
                  onChange={() => toggle(d.id)}
                  className="h-4 w-4 accent-emerald-500"
                />
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">{d.ref}</Td>
              <Td className="font-mono text-muted-foreground">{d.username}</Td>
              <Td>
                <CopyHash
                  value={d.address}
                  href={`https://bscscan.com/address/${d.address}`}
                  head={10}
                  tail={8}
                />
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">
                {fixed2(d.amountReceived)}
              </Td>
              <Td align="right" className="font-mono font-semibold text-ginox-green">
                {fixed2(d.onChainBalance)}
              </Td>
            </tr>
          )}
        />
      )}

      {/* One bar rather than two buttons pinned to opposite edges of a wide table —
          at this width they read as unrelated controls. Everything the decision needs
          sits together: what is selected, how much that is, and the button. */}
      {rows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-border/70 bg-white/[0.02] px-4 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={toggleAll}>
              {selected.length === rows.length ? "Clear selection" : "Select all"}
            </Button>
            <span className="text-xs text-muted-foreground">
              {selected.length === 0 ? (
                `Nothing selected · ${rows.length} address${rows.length === 1 ? "" : "es"} waiting`
              ) : (
                <>
                  {selected.length} of {rows.length} selected ·{" "}
                  <span className="font-mono font-semibold text-ginox-green">
                    {fixed2((totalCents(selectedBalances) / 100).toFixed(2))}
                  </span>{" "}
                  to move
                </>
              )}
            </span>
          </div>
          <Button disabled={!canSweep} onClick={sweep}>
            {sweeping
              ? "Sweeping…"
              : `Sweep ${selected.length || ""} address${selected.length === 1 ? "" : "es"}`.trim()}
          </Button>
        </div>
      ) : null}

      <div className="space-y-4 pt-4">
        <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Sweep history
        </h2>
        {history.error ? (
          <ErrorBanner error={history.error} onRetry={() => history.refetch()} />
        ) : (
          <>
            <QueueTable
              columns={HISTORY_COLUMNS}
              isLoading={history.isLoading}
              rows={historyRows}
              renderRow={(r) => (
                <tr key={r.id} className="transition-colors hover:bg-white/[0.02]">
                  <Td className="whitespace-nowrap font-mono text-muted-foreground">
                    {formatDateTime(r.createdAt)}
                  </Td>
                  <Td align="right" className="font-mono text-muted-foreground">{r.ref}</Td>
                  <Td className="font-mono text-muted-foreground">{r.username}</Td>
                  <Td><CopyHash value={r.fromAddress} head={6} tail={4} /></Td>
                  <Td><CopyHash value={r.toAddress} head={6} tail={4} /></Td>
                  <Td align="right" className="font-mono">{fixed2(r.amount)}</Td>
                  <Td align="right" className="font-mono text-muted-foreground">
                    {r.gasFee}
                  </Td>
                  <Td>
                    <CopyHash
                      value={r.txHash}
                      href={`https://bscscan.com/tx/${r.txHash}`}
                      head={8}
                      tail={6}
                    />
                  </Td>
                </tr>
              )}
            />
            {history.hasNextPage ? (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => history.fetchNextPage()}
                  disabled={history.isFetchingNextPage}
                >
                  {history.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </QueueLayout>
  );
}

// Wallet context is scoped to this page, exactly as the GUSD sweep does it — no
// other admin screen needs a wallet, and the app's root QueryClientProvider is
// already an ancestor so wagmi's hooks resolve.
export function DepositSweepPage() {
  return (
    <Web3Provider>
      <SweepPage />
    </Web3Provider>
  );
}
